import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  OnDestroy,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { HttpClient } from '@angular/common/http';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { IonInput, IonItem, ToastController } from '@ionic/angular';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { marker } from '@jsverse/transloco-keys-manager/marker';
import { firstValueFrom } from 'rxjs';
import { map, merge, startWith } from 'rxjs';
import { AccountDetailsApi } from '../../../models';
import { EnvService } from '../../../services/env.service';
import {
  ChangeEmailErrorKey,
  IdentityService,
  mapChangeEmailError,
  normalizeLoginEmail,
} from '../../../services/identity.service';
import { MyInfoService } from '../../../services/my-info.service';
import { PanelHostService } from '../../../services/panel-host.service';
import { SessionService } from '../../../services/session.service';
import { ButtonComponent } from '../../ui/button/button.component';
import { ContentContainerComponent } from '../../ui/content-container/content-container.component';
import { InlineAlertComponent } from '../../ui/inline-alert/inline-alert.component';
import { PanelBodyComponent } from '../../ui/panel-body/panel-body.component';

@Component({
  selector: 'app-change-email-panel',
  imports: [
    ReactiveFormsModule,
    TranslocoDirective,
    ButtonComponent,
    ContentContainerComponent,
    InlineAlertComponent,
    PanelBodyComponent,
    IonInput,
    IonItem,
  ],
  templateUrl: './change-email-panel.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChangeEmailPanelComponent implements OnDestroy {
  private readonly panelHost = inject(PanelHostService);
  private readonly identityService = inject(IdentityService);
  private readonly sessionService = inject(SessionService);
  private readonly myInfoService = inject(MyInfoService);
  private readonly http = inject(HttpClient);
  private readonly envService = inject(EnvService);
  private readonly transloco = inject(TranslocoService);
  private readonly toastController = inject(ToastController);
  private readonly formBuilder = inject(FormBuilder);

  readonly submitting = signal(false);
  readonly errorMessage = signal<ChangeEmailErrorKey | 'same-email' | null>(null);
  readonly submitAttempted = signal(false);
  private readonly currentEmail = signal<string | null>(null);

  readonly form = this.formBuilder.nonNullable.group({
    email: ['', [Validators.required, Validators.email]],
  });

  private readonly formInvalid = toSignal(
    merge(this.form.statusChanges, this.form.valueChanges).pipe(
      startWith(null),
      map(() => this.form.invalid),
    ),
    { initialValue: this.form.invalid },
  );

  readonly showValidationErrors = computed(
    () => this.submitAttempted() && this.formInvalid(),
  );

  constructor() {
    effect(() => {
      if (this.panelHost.activePanel() !== 'change-email') {
        return;
      }

      this.panelHost.registerDirtyPredicate(() => this.form.dirty && !this.submitting());

      this.errorMessage.set(null);
      this.submitAttempted.set(false);
      this.currentEmail.set(null);
      this.form.reset({ email: '' });

      const userId = this.sessionService.authUserId();
      if (userId === null) {
        return;
      }

      void firstValueFrom(
        this.http.get<AccountDetailsApi>(`${this.envService.apiUrl}/users/${userId}/info`),
      )
        .then((user) => {
          this.currentEmail.set(user.email);
        })
        .catch(() => {
          this.currentEmail.set(null);
        });
    });
  }

  ngOnDestroy(): void {
    this.panelHost.clearDirtyPredicate();
  }

  cancel(): void {
    void this.panelHost.dismiss('/profile');
  }

  async submit(): Promise<void> {
    this.form.markAllAsTouched();
    this.errorMessage.set(null);
    this.submitAttempted.set(true);

    if (this.form.invalid || this.submitting()) {
      return;
    }

    const rawEmail = this.form.controls.email.value;
    const normalizedEmail = normalizeLoginEmail(rawEmail);
    const currentEmail = this.currentEmail();
    if (currentEmail !== null && normalizeLoginEmail(currentEmail) === normalizedEmail) {
      this.errorMessage.set('same-email');
      return;
    }

    this.submitAttempted.set(false);
    this.submitting.set(true);

    try {
      const newEmail = await firstValueFrom(this.identityService.changeEmail(rawEmail));
      this.currentEmail.set(newEmail);
      this.form.markAsPristine();
      this.myInfoService.reloadActiveUser();

      const toast = await this.toastController.create({
        message: this.transloco.translate(marker('identity.change-email.success'), {
          email: newEmail,
        }),
        duration: 4000,
        color: 'success',
      });
      await toast.present();
      void this.panelHost.dismiss('/profile');
    } catch (error) {
      this.errorMessage.set(mapChangeEmailError(error));
    } finally {
      this.submitting.set(false);
    }
  }
}
